import { useCallback, useEffect, useState } from "react";
import { CollectionServerUser, DataExportRequest, JSONOrgInfo, AdminUserBase, OrgInfo, RequestParams, RequestParsedPayload, UserProfile } from "@preveil-api";
import {
  Account, ApprovalGroup, ApprovalRequestStatus, collectionApi, DataExportClosedStates, GetUsersOrgsByEntityIdRequestsApiResponse, GetUsersOrgsByEntityIdGroupsApiResponse, UserRequest, dayjs, OrganizationInformation,
  Contacts, useAppDispatch, useGetPopulatedRequests, useLightweightUsers, RequestTypes, isSameUser, UserResultsMode, UserResultsModeType, PendingAgStatus, PendingAgStatusType,
  ResolvableOrgUser
} from "src/common";
import { adminActions } from "src/store";

/* Description: This is a custom hook for getting the organization info, this hook is used in more than one admin
   component. It returns the above OrganizationInformation object.

   user_results (defaults to full) controls what gets fetched and resolved:
     - full: fetches the full per-user list (data.org_users); approvers, export_request, and actions_pending all resolve.
     - lightweight: sends the same org-info request as "none", plus a lightweight list data.lightweight_org_users,
       used to resolve approver name/role and actions_pending; optional pending-AG status; export_request stays unresolved.
     - none: skips the user list entirely; approvers keep a real address but null name/role; export_request
       and actions_pending stay unresolved.
*/
export function useGetOrgInfo(
  current_account: Account,
  org_info: OrgInfo,
  user_results: UserResultsModeType = UserResultsMode.full,
  pending_ag_status: PendingAgStatusType = PendingAgStatus.exclude,
) {
  const [error, setError] = useState<unknown>();
  const [json_org_info, setOrgInfo] = useState<JSONOrgInfo>();
  const [org_users, setOrgUsers] = useState<CollectionServerUser[]>([]);
  const [approval_group, setApprovalGroup] = useState<ApprovalGroup[]>([]);
  const [export_request, setExportRequest] = useState<any>(); // TO DO: leaving this untyped for now as it will be used while working on the Data Export ticket (still need to figure that out)
  const [total_request_rows, setTotalRequestRows] = useState<number>(0);
  const [data, setData] = useState<OrganizationInformation>();
  const [no_recovery_or_unclaimed, setNoRecoveryOrUnclaimed] = useState<boolean>();
  const [pending_population, setPendingPopulation] = useState<{ requests: UserRequest[], org_users: ResolvableOrgUser[], approval_groups: ApprovalGroup[], is_history: boolean }>();
  const account_ids = Account.getAccountIdentifiers(current_account);
  const [getOrgInfo] = collectionApi.endpoints.getUsersOrgsByEntityId.useLazyQuery();
  const [getOrgAG] = collectionApi.endpoints.getUsersOrgsByEntityIdGroups.useLazyQuery();
  const [getOrgRequests] = collectionApi.endpoints.getUsersOrgsByEntityIdRequests.useLazyQuery();
  const {
    data: populated_requests_data,
    error: populated_requests_error,
    refreshPopulatedRequests,
    clearPopulatedRequests,
  } = useGetPopulatedRequests(current_account);
  const {
    data: lightweight_org_users,
    pending_ag_status_by_user,
    error: lightweight_error,
    refresh: refreshLightweightUsers,
  } = useLightweightUsers(current_account, org_info.org_id, pending_ag_status === PendingAgStatus.include);
  const dispatch = useAppDispatch();
  const lightweight_users_ready = user_results !== UserResultsMode.lightweight || lightweight_org_users !== undefined;

  // Description: Re-resolves approval group approvers whenever approval_group, lightweight_org_users, or user_results change.
  useEffect(() => {
    resolveApprovalGroupApprovers();
  }, [approval_group, lightweight_org_users, user_results]);

  // Description: Forwards useLightweightUsers' own fetch errors into this hook's error state.
  useEffect(() => {
    if (lightweight_error) setError(lightweight_error);
  }, [lightweight_error]);

  // Description: Re-checks on every pending_population/lightweight_org_users change whether the deferred request
  // population can now run; does nothing until the lightweight list (if needed) is ready.
  useEffect(() => {
    resolvePendingPopulation();
  }, [pending_population, lightweight_org_users]);

  // Description: Sets data only once everything it depends on has resolved — org info, approval groups, and the
  // no-recovery-or-unclaimed check, plus the background lightweight-user-list and pending-AG-status loads.
  useEffect(() => {
    const pending_ag_status_ready = user_results !== UserResultsMode.lightweight || pending_ag_status !== PendingAgStatus.include || pending_ag_status_by_user !== undefined;
    if (!!populated_requests_data && !!json_org_info && no_recovery_or_unclaimed !== undefined && lightweight_users_ready && pending_ag_status_ready) {
      setData({
        org_info: json_org_info,
        approval_groups: approval_group,
        org_users,
        total_request_rows: total_request_rows - populated_requests_data.invalid_requests_count,
        org_requests: populated_requests_data.requests,
        is_history_request: populated_requests_data.is_history,
        pending_group_updates: populated_requests_data.pending_group_updates,
        lightweight_org_users: lightweight_org_users ?? [],
        pending_ag_status_by_user: pending_ag_status_by_user ?? {},
        export_request,
      });
      if (user_results === UserResultsMode.full) {
        const pending_requests = orgHasPendingRequests(populated_requests_data.requests);
        const actions_pending = pending_requests || no_recovery_or_unclaimed;
        dispatch(adminActions.setActionsPending(actions_pending));
      }
    }
  }, [populated_requests_data, populated_requests_error, no_recovery_or_unclaimed, approval_group, lightweight_org_users, pending_ag_status_by_user,
    json_org_info, org_users, total_request_rows, export_request, user_results, pending_ag_status]);

  // Description: Fills in approvers' name/role once lightweight_org_users loads, committing the result back into
  // approval_group state directly so it only updates when something was actually resolved.
  function resolveApprovalGroupApprovers() {
    if (user_results !== UserResultsMode.lightweight || !lightweight_org_users || lightweight_org_users.length === 0) return;
    const lightweight_users_by_id = new Map(
      lightweight_org_users.filter((user: AdminUserBase) => !!user.user_id).map((user: AdminUserBase) => [user.user_id.toLowerCase(), user]),
    );
    let changed = false;
    const resolved = approval_group.map((group: ApprovalGroup) => {
      const approvers = group.approvers.map((approver: UserProfile) => {
        if (!approver.address) return approver;
        const user = lightweight_users_by_id.get(approver.address.toLowerCase());
        if (!user || (approver.name === user.display_name && approver.role === user.entity_role)) return approver;
        changed = true;
        return { ...approver, name: user.display_name, role: user.entity_role };
      });
      return new ApprovalGroup(group.approval_group_id, group.name, approvers, group.required_approvers, group.group_version, group.roles, group.is_deleted);
    });
    if (changed) setApprovalGroup(resolved);
  }

  // Description: Defers resolving requester/for_users names until the lightweight list (if any) is ready, so
  // populateRequestInfo always sees the complete user list instead of an empty placeholder.
  function resolvePendingPopulation() {
    if (!pending_population || !lightweight_users_ready) return;
    const effective_org_users = user_results === UserResultsMode.lightweight ? (lightweight_org_users ?? []) : pending_population.org_users;
    refreshPopulatedRequests(pending_population.requests, effective_org_users, pending_population.approval_groups, pending_population.is_history);
    setPendingPopulation(undefined);
  }

  // Description: Resolves request population immediately when the active mode has no user list to wait for
  // (full/none, or lightweight with the list already loaded); otherwise stashes it in pending_population for
  // resolvePendingPopulation to pick up once the lightweight list arrives.
  function resolvePopulation(requests: UserRequest[], org_users: ResolvableOrgUser[], approval_groups: ApprovalGroup[], is_history: boolean) {
    if (!lightweight_users_ready) {
      setPendingPopulation({ requests, org_users, approval_groups, is_history });
      return;
    }
    const effective_org_users = user_results === UserResultsMode.lightweight ? (lightweight_org_users ?? []) : org_users;
    refreshPopulatedRequests(requests, effective_org_users, approval_groups, is_history);
  }

  /* Description: Get the organization info from the backend and set the org users list.
     Also calls the get approval groups function */
  async function getOrganizationInfo(request_params?: RequestParams) {
    const params = {
      account_ids,
      body: {
        entity_id: org_info.org_id,
        user_results: user_results === UserResultsMode.full ? undefined : UserResultsMode.none,
      },
    };
    if (user_results === UserResultsMode.lightweight) {
      refreshLightweightUsers();
    }
    getOrgInfo(params)
      .unwrap()
      .then((response: JSONOrgInfo) => {
        setOrgInfo(response);
        if (user_results === UserResultsMode.full) {
          const org_users = response.users.filter((user: CollectionServerUser) => !user.deleted);
          setOrgUsers(org_users);
          new Contacts(current_account.user_id, null, org_users).updateContactSessions();
          getOrganizationApprovalGroups(response, org_users, request_params);
        } else {
          getOrganizationApprovalGroups(response, [], request_params);
        }
      })
      .catch((error: any) => {
        setError(error);
      });
  }

  /* Description: Gets the organization approval group from the backend and creates a new ApprovalGroup object. */
  async function getOrganizationApprovalGroups(
    data: JSONOrgInfo,
    org_users: CollectionServerUser[],
    request_params?: RequestParams,
  ) {
    const params = {
      account_ids,
      body: {
        entityId: org_info.org_id,
        sinceRevId: -1,
      },
    };
    getOrgAG(params)
      .unwrap()
      .then((response: GetUsersOrgsByEntityIdGroupsApiResponse) => {
        const approval_groups: ApprovalGroup[] = [];
        response.groups.forEach((ag) => {
          const approvers = ag.group.approvers
            .map((approver) => {
              const user = org_users.find(
                (org_user) => isSameUser(org_user.user_id, approver.user_id),
              );
              return { name: user?.display_name || null, address: user?.user_id || approver.user_id, role: user?.entity_metadata?.role || "" };
            })
            .filter((approver) => !!approver);
          const approval_group_roles = [];
          const roled_approval_groups = data.roled_approval_groups as any;
          for (const role_name in data.roled_approval_groups) {
            const role = roled_approval_groups[role_name];
            role.group_id === ag.id && approval_group_roles.push(role_name);
          }
          approval_groups.push(
            new ApprovalGroup(
              ag.id,
              ag.name,
              approvers,
              ag.group.optionals_required,
              ag.version,
              approval_group_roles,
              ag.is_deleted,
            ),
          );
        });
        setApprovalGroup(approval_groups);
        const updated_org_users = org_users.map((user: CollectionServerUser) => {
          const recovery_group = approval_groups.find(
            (ag) => ag.approval_group_id === user.entity_metadata.recovery_group_id,
          );
          // Issues with V1 not lowercasing user_ids, so we're doing it here to avoid issues/inconsistencies throughout admin
          if (!!recovery_group) {
            return { ...user, recovery_group };
          }
          return user;
        });
        setOrgUsers(updated_org_users);
        if (user_results === UserResultsMode.full) {
          const users_without_recovery = updated_org_users.filter(
            (user: CollectionServerUser) => !user.entity_metadata.recovery_group_pending).filter((user: CollectionServerUser) => !user.recovery_group && user.claimed
          ).length;
          dispatch(adminActions.setUsersWithoutRecovery(users_without_recovery));
        }
        const pending = updated_org_users.some(
          (user: CollectionServerUser) => user.entity_metadata.recovery_group_pending || user.entity_metadata.pending_recovery_group_id ||
                  !user.recovery_group || !user.claimed
        );
        setNoRecoveryOrUnclaimed(pending);
        getOrganizationRequests(updated_org_users, approval_groups, request_params);
      })
      .catch((error: any) => {
        setError(error);
      });
  }

  /* Description: Returns true if there are pending requests for any user (only counts request types that show up in the Due column in Users list) */
  function orgHasPendingRequests(requests: UserRequest[]): boolean {
    return requests.some((request) =>
      request.type === RequestTypes.member_rekey_and_set_approval_group ||
      request.type === RequestTypes.delete_user ||
      request.type === RequestTypes.change_admin_status
    );
  }

  /* Description: Gets the organization requests from the backend, populates the user request objects by calling the
    use get populated requests hook and  */
  async function getOrganizationRequests(
    org_users: ResolvableOrgUser[],
    approval_groups: ApprovalGroup[],
    request_params?: RequestParams,
  ) {
    clearPopulatedRequests();
    if (!!request_params) {
      const params = {
        account_ids,
        body: {
          status: request_params?.status,
          hideExpired: request_params?.hideExpired,
          limit: request_params?.limit,
          offset: request_params?.offset,
          entityId: org_info.org_id,
        },
      };
      await getOrgRequests(params)
        .unwrap()
        .then(async (response: GetUsersOrgsByEntityIdRequestsApiResponse) => {
          setTotalRequestRows(response.total_rows);
          if (!request_params.history) {
            const export_requests = response.requests.filter(
              (r) =>
                r.type === "export" &&
                !!current_account &&
                isSameUser(r.requester, current_account.user_id),
            );
            const export_request = export_requests.find(
              (r) => !DataExportClosedStates.includes(r.status),
            );
            if (!!export_request && user_results === UserResultsMode.full) {
              const parsed_payload = JSON.parse(export_request.payload) as RequestParsedPayload;
              const requester_user = org_users.find(
                (user) => isSameUser(user.user_id, export_request.requester),
              );
              const metadata = export_request.metadata as DataExportRequest;
              const export_req = {
                request_id: export_request.request_id,
                status: export_request.status,
                requester: {
                  name: requester_user?.display_name || null,
                  address: requester_user?.user_id || "",
                },
                /* approval_group: approval_groups.find(
                  (ag) => ag.uid === export_request.group_id.String(),
                ), // TO DO: test this */
                timestamp: export_request.timestamp,
                type: export_request.type,
                export_content: {
                  from: dayjs.utc(metadata.from).local().format("l"),
                  until: dayjs.utc(metadata.until).local().format("l"),
                  include_email: metadata.include_emails,
                  include_drive: metadata.include_files,
                  include_log: metadata.include_logs,
                  include_org_acl_report: metadata.include_org_acl_report,
                },
                expiration: dayjs.utc(export_request.expiration).local().format("lll"),
                // raw_request: export_request,
                for_users: (parsed_payload.data as DataExportRequest).users.map((user) => {
                  const u = org_users.find((o_u) => isSameUser(user.user_id, o_u.user_id));
                  return { name: u?.display_name || null, address: u?.user_id || "" };
                }),
              };
              setExportRequest(export_req);
              // TO DO: finish this part during Data Export ticket.
              /* if ( !!export_req.approval_group && export_req.approval_group.approvers.find(
                  (approver) => approver.address.toLowerCase() === current_account.user_id,
                )
              ) {
                const approver_shards = [current_account];
              } */
            }
          }
          const response_requests = !request_params.history ? response.requests
            .filter((request) => request.type !== "export") : response.requests;
          if (!request_params.export) {
            const requests = response_requests
              .map((request) => {
                return new UserRequest(
                  request.request_id,
                  request.metadata,
                  request.payload,
                  request.status,
                  request.requester,
                  [],
                  null,
                  request.group_id,
                  !history && request.status === ApprovalRequestStatus.pending,
                );
              });
            resolvePopulation(requests, org_users, approval_groups, !!request_params.history);
          }
        })
        .catch((msg) => {
          setError(msg);
        });
    } else {
      resolvePopulation([], org_users, approval_groups, false);
    }
  }

  /* Description: Callback for get org info hook. If we are refreshing from the history tab of approval requests, we already
  have the org users approval group and so we call the organization requests directly */
  const refresh = useCallback(
    (
      request_params?: RequestParams,
      history?: { orgUsers: ResolvableOrgUser[]; approvalGroups: ApprovalGroup[] },
    ) => {
      setError(undefined);
      if (!!history) {
        setApprovalGroup(history.approvalGroups);
        getOrganizationRequests(history.orgUsers, history.approvalGroups, request_params);
      } else {
        getOrganizationInfo(request_params);
      }
    },
    [],
  );

  return {
    data,
    error,
    refresh,
  };
}

